<template>
    <section>
        <!-- Void Button -->
        <ClButton
            type="secondary"
            :rounded="false"
            :disabled="!hasLocalMailItemId"
            aria-label="void transaction"
            @click="showModal = true"
        >
            <span v-if="voidStateIsVoided">{{ voidButtonCustomization.successText }}</span>
            <span v-else>{{ voidButtonCustomization.text }}</span>
        </ClButton>
        <!-- Void Modal -->
        <ClModal
            v-model="showModal"
            maxWidth="25rem"
            @close="cancelButtonHandler()"
        >
            <v-card class="void-modal">
            <!-- Modal Header -->
                <v-card-title class="void-modal__title">
                    Void Mail Item Transaction
                    <v-btn
                        icon
                        @click="cancelButtonHandler()"
                        >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                    <!-- Modal Body -->
                <v-card-text class="void-modal__body">
                    <div class="reason-selection-header">
                        <p>Select a reason to Void this Mail Item Transaction</p>
                    </div>
                    <div class="reason-selection-container">
                        <div
                            v-for="( reason, index ) in voidReasonList"
                            :key="index"
                            class="voidable-reason-list"
                        >
                            <input
                                :id="reason.id"
                                v-model="models.selectedReason"
                                :value="reason.label"
                                type="radio"
                            />
                            <label :for="reason.id">
                                {{ reason.label }}
                            </label>
                        </div>
                    </div>
                    <div class="other-reason-text-area-container">
                        <label for="other-reason-text-area">
                            Other Reason:
                        </label>
                        <v-textarea
                            id="other-reason-text-area"
                            ref="other-reason-text-area"
                            v-model="models.otherReasonTextArea"
                            :class="{ 'is-disabled': !selectedReasonIsOther }"
                            :disabled="!selectedReasonIsOther"
                            outlined
                            dense
                            hideDetails
                        />
                    </div>
                </v-card-text>
                <v-card-actions class="void-modal__actions">
                    <ClButton
                        :disabled="voidStateIsInFlight"
                        type="secondary"
                        :rounded="false"
                        @click="cancelButtonHandler()"
                    >
                        Cancel
                    </ClButton>
                    <ClButton
                        :loading="voidStateIsInFlight"
                        :disabled="!canVoidTransaction || voidStateIsInFlight"
                        :rounded="false"
                        @click="voidMailItemTransaction()"
                    >
                        Void Transaction
                    </ClButton>
                </v-card-actions>
            </v-card>
        </ClModal>
    </section>
</template>

<script>
// Vuex
import { mapState, mapGetters } from 'vuex';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import ErrorUtil from '@/utils/ErrorUtil';

const VoidStates = {
    INITIAL: 'INITIAL',
    IN_FLIGHT: 'IN_FLIGHT',
    VOIDED: 'VOIDED',
};

/**
 * Events Available
 * @emits voidModalClose
 * @emits voidModalCancel
 * @emits voidItemSuccess
 * @emits voidItemFailure
 * @emits voidItemAlways
 */
export default {
    name: 'ButtonModalVoidTransaction',
    props: {
        mailItemId: {
            type: Number,
            required: true,
        },
        voidButtonCustomization: {
            type: Object,
            required: false,
            default: () => ( { iconClassName: 'cancel', text: 'Void', successText: 'Voided' } ),
        },
    },
    data() {
        return {
            showModal: false,
            voidState: VoidStates.INITIAL,

            models: {
                selectedReason: '',
                otherReasonTextArea: '',
            },
            localMailItemId: null,

            voidReasonList: [
                {
                    id: 'mistake',
                    label: 'Created By Mistake',
                },
                {
                    id: 'late',
                    label: 'Would Not Arrive on Time',
                },
                {
                    id: 'shipping',
                    label: 'Need to Change Shipping Address',
                },
                {
                    id: 'delivery',
                    label: 'Need to Change Delivery Method',
                },
                {
                    id: 'notPickedUp',
                    label: 'Not Picked Up',
                },
                {
                    id: 'other',
                    label: 'Other',
                },
            ],
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
        } ),
        /** @returns { Boolean } */
        hasLocalMailItemId() {
            return !!this.localMailItemId;
        },
        /** @returns { Boolean } */
        hasSelectedAReason() {
            return this.models.selectedReason !== '';
        },
        /** @returns { Boolean } */
        selectedReasonIsOther() {
            return this.models.selectedReason === 'Other';
        },
        /** @returns { Boolean } */
        canVoidTransaction() {
            return ( this.selectedReasonIsOther && ( this.models.otherReasonTextArea !== '' ) )
                || ( this.hasSelectedAReason && !this.selectedReasonIsOther );
        },

        /** @returns { Boolean } */
        voidStateIsInitial() {
            return this.voidState === VoidStates.INITIAL;
        },
        /** @returns { Boolean } */
        voidStateIsInFlight() {
            return this.voidState === VoidStates.IN_FLIGHT;
        },
        /** @returns { Boolean } */
        voidStateIsVoided() {
            return this.voidState === VoidStates.VOIDED;
        },
    },
    watch: {
        selectedReasonIsOther() {
            if ( this.selectedReasonIsOther === false ) {
                this.models.otherReasonTextArea = '';
            } else {
                this.$nextTick( () => {
                    this.$refs[ 'other-reason-text-area' ].focus();
                } );
            }
        },
        mailItemId: {
            handler: 'init',
            immediate: true,
        },
    },
    methods: {
        init() {
            this.localMailItemId = this.mailItemId;
            this.voidState = VoidStates.INITIAL;
        },
        closeModal() {
            this.showModal = false;
            this.models.selectedReason = '';
            this.models.otherReasonTextArea = '';
            this.$emit( 'voidModalClose' );
        },
        cancelButtonHandler() {
            this.closeModal();
            this.$emit( 'voidModalCancel' );
        },
        clearLocalMailItemId() {
            this.localMailItemId = null;
        },

        async voidMailItemTransaction() {
            this.voidState = VoidStates.IN_FLIGHT;
            try {
                await LogisticsApi.voidMailItem( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    mailItemId: this.mailItemId,
                    reason: this.selectedReasonIsOther
                        ? this.models.otherReasonTextArea
                        : this.models.selectedReason,
                } );
                await this.$store.dispatch( 'snackbar', {
                    message: 'Mail Item Transaction Voided',
                    type: 'success',
                    duration: 5000,
                }, { root: true } );
                this.voidState = VoidStates.VOIDED;
                this.$emit( 'voidItemSuccess' );
                this.clearLocalMailItemId();
            } catch ( error ) {
                await this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                this.voidState = VoidStates.INITIAL;
                this.$emit( 'voidItemFailure' );
            } finally {
                this.closeModal();
                this.$emit( 'voidItemAlways' );
            }
        },
    },
};
</script>

<style scoped lang="scss">
.modal-card-foot {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
}

.reason-selection-header {
    font-weight: 600;
    margin-bottom: 1rem;
}
.voidable-reason-list {
    >label {
        margin-left: 0.5rem;
    }
}
.other-reason-text-area-container {
    display: flex;
    flex-flow: column nowrap;
    margin-left: 1.4rem;
}
.is-disabled {
    cursor: not-allowed;
}
.void-modal {
    &__title {
        display: flex;
        justify-content: space-between;
        background-color: var(--v-primary-base);
        color: $colors-white;
        .v-icon {
            color: $colors-white;
        }
    }
    & &__body {
        padding: 1.25rem;
        padding-top: 1rem;
    }
    &__actions {
        display: flex;
        justify-content: flex-end;
    }
}
</style>
